// apps/widget/src/components/metric-card.tsx
//
// One score, explained: where it applies on this visitor's face, what it
// measures and what helps. A number with no explanation reads as a verdict;
// the same number with its area marked and a next step reads as analysis.

import { BAND_COLORS } from '../lib/theme';
import { t, metricLabel } from '../lib/i18n';
import type { ReportMetric } from '../lib/api';
import type { FaceRegion } from '@skinvizo/shared';
import { useScan } from '../state';
import { AnnotatedFace } from './report-visual';
import { ScoreBar } from './score-bar';

interface Props {
  metric: ReportMetric;
  compact?: boolean;
  active?: boolean;
  onSelect?: (key: string) => void;
}

export function MetricCard({ metric, compact, active, onSelect }: Props) {
  const { state } = useScan();
  const colour = BAND_COLORS[metric.band as keyof typeof BAND_COLORS] ?? BAND_COLORS.average;
  // Copy is keyed by metric; the model's own advice, when it gives one, is
  // specific to this face and wins over the general tip.
  const about = t(`metricInfo.${metric.key}.about`);
  const tip = metric.advice ?? t(`metricInfo.${metric.key}.tip`);
  const hasCopy = !about.startsWith('metricInfo.');

  const body = (
    <>
      {/* The thumbnail needs this tab's capture. A report opened from a link
          has no photo — it was deleted after analysis — so the card goes
          without rather than showing someone else's face. */}
      {state.capture && metric.regions.length > 0 && (
        <span className="gs-metric-thumb" aria-hidden="true">
          <AnnotatedFace
            capture={state.capture}
            metric={metric.key}
            score={metric.score}
            regions={metric.regions as FaceRegion[]}
            colour={colour}
            label=""
          />
        </span>
      )}

      <span className="gs-metric-main">
        <span className="gs-metric-head">
          <span className="gs-metric-label">{metricLabel(metric.key, metric.label)}</span>
          {/* Band as text as well as colour — never colour alone. */}
          <span className="gs-metric-band" style={{ color: colour }}>
            {t(`report.band.${metric.band}`)}
          </span>
        </span>

        <ScoreBar score={metric.score} band={metric.band} />

        {hasCopy && <span className="gs-metric-advice">{about}</span>}
        {!compact && hasCopy && (
          <span className="gs-metric-tip">
            <b>{t('metricInfo.tip')}:</b> {tip}
          </span>
        )}
      </span>
    </>
  );

  // Only a button when there is something to activate. A control that does
  // nothing is worse than plain text for anyone on a screen reader.
  if (!onSelect) {
    return (
      <div id={`gs-metric-${metric.key}`} className="gs-metric" data-compact={compact || undefined}>
        {body}
      </div>
    );
  }

  return (
    <button
      id={`gs-metric-${metric.key}`}
      type="button"
      className="gs-metric gs-metric--interactive"
      data-compact={compact || undefined}
      data-active={active || undefined}
      aria-pressed={active}
      onClick={() => onSelect(metric.key)}
    >
      {body}
    </button>
  );
}
